import React, { createContext, useState, useContext, ReactNode } from 'react';

// Types d'informations qui peuvent être affichées dans la colonne de droite
export enum InfoDisplayType {
  DEFAULT = 'DEFAULT',  // Affichage par défaut (aperçu)
  CONTRACT = 'CONTRACT', // Informations du contrat
  VEHICLE = 'VEHICLE',  // Informations du véhicule
  CUSTOMER = 'CUSTOMER', // Informations du client
  QUOTE = 'QUOTE',     // Détails du devis
}

interface InfoDisplayContextType {
  currentDisplay: InfoDisplayType;
  showInfo: (type: InfoDisplayType) => void;
  hideInfo: () => void;
  isInfoVisible: (type: InfoDisplayType) => boolean;
}

// Création du contexte avec une valeur par défaut
export const InfoDisplayContext = createContext<InfoDisplayContextType>({
  currentDisplay: InfoDisplayType.DEFAULT,
  showInfo: () => {},
  hideInfo: () => {},
  isInfoVisible: () => false,
});

interface InfoDisplayProviderProps {
  children: ReactNode;
}

export const InfoDisplayProvider: React.FC<InfoDisplayProviderProps> = ({ children }) => {
  // État pour suivre quel type d'information est actuellement affiché
  const [currentDisplay, setCurrentDisplay] = useState<InfoDisplayType>(InfoDisplayType.DEFAULT);

  // Afficher un type d'information spécifique
  const showInfo = (type: InfoDisplayType) => {
    // Si le même type est déjà affiché, on revient à l'affichage par défaut (toggle)
    if (currentDisplay === type) {
      setCurrentDisplay(InfoDisplayType.DEFAULT);
    } else {
      setCurrentDisplay(type);
    }
  };

  // Revenir à l'affichage par défaut
  const hideInfo = () => {
    setCurrentDisplay(InfoDisplayType.DEFAULT);
  };

  // Vérifier si un type d'information spécifique est actuellement affiché
  const isInfoVisible = (type: InfoDisplayType) => {
    return currentDisplay === type;
  };

  const value = {
    currentDisplay,
    showInfo,
    hideInfo,
    isInfoVisible,
  };

  return (
    <InfoDisplayContext.Provider value={value}>
      {children}
    </InfoDisplayContext.Provider>
  );
};

// Hook personnalisé pour utiliser ce contexte
export const useInfoDisplay = () => {
  const context = useContext(InfoDisplayContext);

  if (!context) {
    throw new Error('useInfoDisplay doit être utilisé à l\'intérieur d\'un InfoDisplayProvider');
  }

  return context;
};